import { getIn } from "formik"
import moment from "moment"
import { createPortal } from "react-dom"
import { v4 as uuidV4 } from "uuid"
import validator from "validator"
import * as Yup from "yup"
import logger from "../../../Logger"
import { DATE_FORMAT, TIME_FORMAT } from "../../../utilities/constants"
import RosterTable from "./Form-Elements/RosterTable/roster-table"
import {
  FieldList,
  FieldTable,
  InputCheckbox,
  InputDate,
  InputEmail,
  InputFile,
  InputMultiSelect,
  InputRadio,
  InputRadioCards,
  InputRole,
  InputSelect,
  InputText,
  InputTime,
} from "./Form-Elements"
import {
  FIELD_GROUP_TYPES,
  FIELD_TYPES,
  MAX_ALLOWED_FILE_SIZE_BYTES,
  MAX_ARRAY_LENGTH_VALIDATION_DEFAULT_VALUE,
  MAX_LENGTH_VALIDATION_DEFAULT_VALUE,
  MIN_ARRAY_LENGTH_VALIDATION_DEFAULT_VALUE,
  MIN_LENGTH_VALIDATION_DEFAULT_VALUE,
  OTHER_OPTION_PLACEHOLDER_VALUE,
  VALIDATION_TYPES,
  emailValidationOptions,
  tableGroupValueIdKey,
  validAPINodeEntryMatchRegex,
  validNameFieldMatchRegex,
  validTextEntryMatchRegex,
} from "./dynamic-form.constants"

const pageLogger = logger.child({ module: "Dynamic Form Helper" })

const hasMultipleOptions = (fieldType) => {
  return [FIELD_TYPES.MULTI_OPTIONS, FIELD_TYPES.MULTI_OPTIONS_WITH_STRING_OTHER].includes(fieldType)
}

// Shared `activeWhen` predicate used by both render-time visibility and the yup validation
// schema builder. A field with `activeWhen:{ field, is }` is active only when the sibling
// field's value matches `is` — via `.includes(is)` when the sibling holds an array
// (MULTI_OPTIONS), else strict `=== is` for scalar fields. Keeping this in one place ensures
// visibility and required-validation semantics never drift apart.
export const activeWhenMatches = (activeWhen, values) => {
  if (!activeWhen?.field) return true
  const fieldVal = values?.[activeWhen.field]
  return Array.isArray(fieldVal) ? fieldVal.includes(activeWhen.is) : fieldVal === activeWhen.is
}

const needsOtherField = (fieldType) => {
  return [FIELD_TYPES.OPTIONS_WITH_STRING_OTHER, FIELD_TYPES.MULTI_OPTIONS_WITH_STRING_OTHER].includes(fieldType)
}

const otherValueForField = (field) => {
  let otherValue = null
  if (needsOtherField(field?.type)) {
    if (hasMultipleOptions(field?.type)) {
      for (const valItem of field.value) {
        // Check for first value that don't appear in options
        const selectedOption = field.options.find((opt) => opt.value === valItem)
        if (!selectedOption) {
          otherValue = valItem
          break // for
        }
      }
    } else {
      // Check if the value appears in the options
      const enumVals = field.options?.map((opt) => opt.value) ?? []
      if (enumVals.includes(field.value) === false) {
        otherValue = field.value
      }
    }
  }
  return otherValue
}

const resolvedValueForField = (field) => {
  let resolvedFieldVal
  const otherVal = otherValueForField(field)
  if (hasMultipleOptions(field?.type)) {
    resolvedFieldVal = otherVal
      ? field.value.map((origVal) => {
          return origVal.toLowerCase() === otherVal.toLowerCase() ? OTHER_OPTION_PLACEHOLDER_VALUE : origVal
        })
      : field.value
  } else {
    resolvedFieldVal = otherVal ? OTHER_OPTION_PLACEHOLDER_VALUE : field.value
  }
  return resolvedFieldVal
}

export const GetSortedFormData = (formData) => {
  if (formData && Array.isArray(formData.layout_data)) {
    formData.layout_data.sort((firstElement, secondElement) => {
      firstElement.groups.sort((e1, e2) => e1["view_order"] - e2["view_order"])
      secondElement.groups.sort((e1, e2) => e1["view_order"] - e2["view_order"])
      return firstElement["layout_order"] - secondElement["layout_order"]
    })
    return formData
  }
  return null
}

export const GetInitialFormValue = ({ formData, formValues }) => {
  // pageLogger.info({ formData }, `form Data received`)
  let _initialValues = {}
  if (formData && Array.isArray(formData.layout_data)) {
    const _layoutDataList = formData.layout_data
    if (Array.isArray(_layoutDataList) && _layoutDataList.length) {
      _layoutDataList.forEach((layoutData) => {
        const _formGroups = layoutData.groups
        _formGroups.forEach((group) => {
          const fields = group.fields
          if (fields && Array.isArray(fields)) {
            const _fieldNodes = {}
            fields.forEach((field) => {
              if ([FIELD_GROUP_TYPES.LIST, FIELD_GROUP_TYPES.TABLE, FIELD_GROUP_TYPES.ROSTER].includes(group.type)) {
                _fieldNodes[group.name] = group.value && Array.isArray(group.value) ? group.value : {}
              } else {
                if (group.type === FIELD_GROUP_TYPES.LIST) {
                  // Initialize "other" field value
                  if (needsOtherField(field.type)) {
                    const otherVal = otherValueForField(field)
                    const resolvedFieldVal = resolvedValueForField(field)
                    _fieldNodes[group.name][`other_${field.name}`] = otherVal || ""
                    _fieldNodes[group.name][field.name] = resolvedFieldVal
                  } else {
                    _fieldNodes[group.name][field.name] = hasMultipleOptions(field.type) ? [...field.value] : field.value
                  }
                } else {
                  // Initialize "other" field value
                  if (needsOtherField(field.type)) {
                    const otherVal = otherValueForField(field)
                    const resolvedFieldVal = resolvedValueForField(field)
                    _fieldNodes[`other_${field.name}`] = otherVal || ""
                    _fieldNodes[field.name] = resolvedFieldVal
                  } else {
                    _fieldNodes[field.name] = hasMultipleOptions(field.field_type) ? [...field.value] : field.value
                  }
                }
              }
            })
            _initialValues = Object.assign({}, _initialValues, _fieldNodes)
          }
        })
      })
    }
    // pageLogger.info({ initialValues: _initialValues }, `InitialValues Created`)
  }
  if (formValues) {
    _initialValues = Object.assign(_initialValues, formValues)
  }
  return _initialValues
}

const getYupValidator = (formField, _schema, group) => {
  formField.validations.forEach((validation) => {
    if (validation.type === VALIDATION_TYPES.SAFETEXT) {
      _schema = _schema.test("safe_text_characters", validation.error_message, (val) => {
        if (val) {
          if (Array.isArray(val)) {
            return validator.matches(val.join(","), validTextEntryMatchRegex)
          }
          return validator.matches(val, validTextEntryMatchRegex)
        }
        return true
      })
    }
    if (validation.type === VALIDATION_TYPES.SAFENAME) {
      _schema = _schema.test("safe_name_characters", validation.error_message, (val) => {
        if (val) {
          return validator.matches(val, validNameFieldMatchRegex)
        }
        return true
      })
    }
    if (validation.type === VALIDATION_TYPES.SAFE_API_NODE_NAME) {
      _schema = _schema.test("safe_api_node_name_characters", validation.error_message, (val) => {
        if (val) {
          if (Array.isArray(val)) {
            return validator.matches(val.join(","), validAPINodeEntryMatchRegex)
          }
          return validator.matches(val, validAPINodeEntryMatchRegex)
        }
        return true
      })
    }
    if (validation.type === VALIDATION_TYPES.VALID_PLAYER_LIST_CSV) {
      _schema = _schema.test("valid_players_csv", validation.error_message, (val) => {
        if (val) {
          let isValid = false
          // Check (UTF8) CSV content
          const rows = val?.utf8?.split("\n")?.map((rowVal) => rowVal.trim())
          if (rows && rows.length > 2) {
            let idx
            for (idx = 1; idx < rows.length; idx++) {
              const [name, email] = rows[idx]?.split(",") ?? [null, null]
              if (name === null || email === null) {
                return false
              }
              if (!validator.matches(name, validNameFieldMatchRegex)) {
                return false
              }
              if (!validator.isEmail(email, emailValidationOptions)) {
                return false
              }
            }
            isValid = true
          }
          return isValid
        }
        return true
      })
    }
    if (validation.type === VALIDATION_TYPES.REQUIRED) {
      // `activeWhen: { field, is }` makes a field required only when a sibling field equals
      // `is` (e.g. a role's Name/Email required only when the row is a Human, not a Bot).
      // Pairs with the render-time visibility filter in RenderFormGroups.
      if (formField.activeWhen?.field) {
        const { field, is } = formField.activeWhen
        // Reuse the shared render-time predicate so visibility and required-validation stay in
        // lockstep: for MULTI_OPTIONS the sibling value is an array (test membership), else
        // scalar strict equality.
        const isMatch = (v) => (Array.isArray(v) ? v.includes(is) : v === is)
        _schema = _schema.when(field, { is: isMatch, then: (s) => s.required(validation.error_message), otherwise: (s) => s })
      } else {
        _schema = _schema.required(validation.error_message)
      }
    }
    if (validation.type === VALIDATION_TYPES.EMAIL) {
      _schema = _schema.test("valid_email", validation.error_message, (val) => {
        if (val) {
          return validator.isEmail(val, emailValidationOptions)
        }
        return true
      })
    }
    if (validation.type === VALIDATION_TYPES.MAX_LENGTH) {
      _schema = _schema.max(validation.value ?? MAX_LENGTH_VALIDATION_DEFAULT_VALUE, validation.error_message)
    }
    if (validation.type === VALIDATION_TYPES.MIN_LENGTH) {
      _schema = _schema.min(validation.value ?? MIN_LENGTH_VALIDATION_DEFAULT_VALUE, validation.error_message)
    }
    if (validation.type === VALIDATION_TYPES.FUTURE_DATETIME) {
      _schema = _schema.test("future_date", validation.error_message, (val) => {
        if (val) {
          const thisMoment = moment(val, `${DATE_FORMAT} ${TIME_FORMAT}`)
          return thisMoment.isAfter()
        }
        return true
      })
    }
    if (validation.type === VALIDATION_TYPES.MAX_FILE_SIZE) {
      _schema = _schema.test("max_file_size", validation.error_message, (value) => value && value.sizeBytes <= MAX_ALLOWED_FILE_SIZE_BYTES)
    }
    if (validation.type === VALIDATION_TYPES.VALID_FILE_EXTENSIONS) {
      _schema = _schema.test("allowed_file_types", validation.error_message, (value) => {
        pageLogger.info({ value }, "value")
        const fileExtension = value?.name?.split(".").pop() ?? null
        return value && validation.allowed_file_extensions?.indexOf(fileExtension) > -1
      })
    }
    if (validation.type === VALIDATION_TYPES.UNIQUE) {
      const fieldName = formField.name ?? null
      const groupFieldName = group?.name ?? null
      _schema = _schema.test("uniqueness_test", validation.error_message, (value, context) => {
        // pageLogger.info({ context }, "unique field validation context")
        // Check for field and group context
        if (fieldName && groupFieldName) {
          // Check context for group values (where the group is the second ancestor of the current field)
          const groupVals = context?.from?.[1]?.value?.[groupFieldName] ?? null
          if (groupVals && Array.isArray(groupVals)) {
            // Check uniqueness of this particular field in the group content
            const matches = groupVals.map((item) => item[fieldName]).filter((item) => item === value)
            if (matches.length > 1) {
              return false // Not Unique
            }
          }
        }

        return true // Unique
      })
    }
  })
  return _schema
}

const getFieldsSchema = (fields, _fieldSchema, timeZones = null, group = null) => {
  if (fields && Array.isArray(fields) && fields.length) {
    fields.forEach((field) => {
      switch (field.type) {
        case FIELD_TYPES.DROPDOWN:
        case FIELD_TYPES.OPTIONS:
        case FIELD_TYPES.OPTION_CARDS:
        case FIELD_TYPES.OPTIONS_WITH_STRING_OTHER: {
          _fieldSchema[field.name] = Yup.string()
          const fieldOptions = field.name === "time_zone" && timeZones ? timeZones : field.options
          _fieldSchema[field.name].oneOf([...fieldOptions.map((option) => option.value)], "No CHOICE AS SUCH")
          break
        }
        case FIELD_TYPES.MULTI_OPTIONS:
        case FIELD_TYPES.MULTI_OPTIONS_WITH_STRING_OTHER:
          _fieldSchema[field.name] = Yup.array().of(Yup.string())
          break
        case FIELD_TYPES.MULTI_DROPDOWN:
          // Coerce a LEGACY scalar string value (a pre-multi-select saved/duplicated roster still
          // holds `role: "SOC Lead"`) into an array before the array validation, so editing such a
          // roster does not fail Formik validation before the submit mapper can normalize it.
          _fieldSchema[field.name] = Yup.array()
            .transform((value, original) => (Array.isArray(value) ? value : original ? [original] : []))
            .of(Yup.string())
          break
        case FIELD_TYPES.ACTION_BTN:
          break
        case FIELD_TYPES.FILE:
          _fieldSchema[field.name] = Yup.mixed()
          break
        default:
          _fieldSchema[field.name] = Yup.string()
          break
      }

      if (field.validations && Array.isArray(field.validations) && field.validations.length) {
        _fieldSchema[field.name] = getYupValidator(field, _fieldSchema[field.name], group)
      } else if (field.type !== FIELD_TYPES.ACTION_BTN) {
        _fieldSchema[field.name] = _fieldSchema[field.name].optional()
      }
    })
  }
  return _fieldSchema
}

const setGroupSchema = (group, _schemaData, timeZones = null) => {
  // Fields List
  if (group?.type === FIELD_GROUP_TYPES.FIELDS) {
    const fieldSchema = getFieldsSchema(group.fields, _schemaData, timeZones)
    _schemaData = { ...fieldSchema }
  } else if (group?.type === FIELD_GROUP_TYPES.LIST || group?.type === FIELD_GROUP_TYPES.TABLE || group?.type === FIELD_GROUP_TYPES.ROSTER) {
    const _subSchema = getFieldsSchema(group.fields, {}, null, group)
    _schemaData[group.name] = Yup.array().of(Yup.object().shape(_subSchema))
    // Apply group level validation rules
    if (group.validations && group.validations.length) {
      group.validations.forEach((validation) => {
        if (validation.type === VALIDATION_TYPES.REQUIRED) {
          _schemaData[group.name] = _schemaData[group.name].required(validation.error_message)
        }
        if (validation.type === VALIDATION_TYPES.MAX_LENGTH) {
          _schemaData[group.name] = _schemaData[group.name].max(
            validation.value ?? MAX_ARRAY_LENGTH_VALIDATION_DEFAULT_VALUE,
            validation.error_message
          )
        }
        if (validation.type === VALIDATION_TYPES.MIN_LENGTH) {
          _schemaData[group.name] = _schemaData[group.name].min(
            validation.value ?? MIN_ARRAY_LENGTH_VALIDATION_DEFAULT_VALUE,
            validation.error_message
          )
        }
      })
    }
  }
}

export const getValidationSchema = (formData, timeZones = null) => {
  let _schemaData = {}
  if (formData && Array.isArray(formData.layout_data)) {
    const _layoutDataList = formData.layout_data
    if (Array.isArray(_layoutDataList) && _layoutDataList.length) {
      _layoutDataList.forEach((layoutData) => {
        const _formGroups = layoutData.groups
        if (_formGroups && Array.isArray(_formGroups)) {
          _formGroups.forEach((group) => {
            setGroupSchema(group, _schemaData, timeZones)
          })
        }
      })
    }
  }
  return Yup.object().shape(_schemaData)
}

const isFieldRequired = (fieldData) => {
  return (
    fieldData &&
    fieldData.validations &&
    Array.isArray(fieldData.validations) &&
    fieldData.validations.find((validation) => validation.type === VALIDATION_TYPES.REQUIRED)
  )
}

// Safely handle field names containing '.' when not a nested field
export const getInNested = (obj, key, isNestedKey) => {
  return isNestedKey ? getIn(obj, key) : obj[key]
}

export const GetFormElements = (
  fieldData,
  fieldName,
  errors,
  timeZones = null,
  isNestedFieldName = false // True if using dot notation in field name to access nested form values
) => {
  const getErrorMessageForFieldName = (fn) => {
    return getInNested(errors, fn, isNestedFieldName)
  }

  switch (fieldData.type) {
    case FIELD_TYPES.DATE:
      return (
        <InputDate
          id={fieldData.id}
          label={fieldData.label}
          name={fieldName}
          placeholder={fieldData.placeholder}
          value={fieldData.value}
          isRequired={isFieldRequired(fieldData)}
          errorMessage={getErrorMessageForFieldName(fieldName)}
          fieldData={fieldData}
          isNestedFieldName={isNestedFieldName}
        ></InputDate>
      )
    case FIELD_TYPES.TIME:
      return (
        <InputTime
          id={fieldData.id}
          label={fieldData.label}
          name={fieldName}
          placeholder={fieldData.placeholder}
          value={fieldData.value}
          isRequired={isFieldRequired(fieldData)}
          errorMessage={getErrorMessageForFieldName(fieldName)}
          fieldData={fieldData}
          isNestedFieldName={isNestedFieldName}
        ></InputTime>
      )
    case FIELD_TYPES.DROPDOWN: {
      const optionData = fieldName === "time_zone" ? timeZones : fieldData.options
      return (
        optionData && (
          <InputSelect
            id={fieldData.id}
            label={fieldData.label}
            name={fieldName}
            optionData={optionData}
            selectedValue={fieldData.value}
            isRequired={isFieldRequired(fieldData)}
            errorMessage={getErrorMessageForFieldName(fieldName)}
            fieldData={fieldData}
            isNestedFieldName={isNestedFieldName}
          ></InputSelect>
        )
      )
    }
    case FIELD_TYPES.STRING:
      return (
        <InputText
          id={fieldData.id}
          label={fieldData.label}
          name={fieldName}
          value={fieldData.value}
          placeholder={fieldData.placeholder}
          rows={fieldData.rows}
          isRequired={isFieldRequired(fieldData)}
          errorMessage={getErrorMessageForFieldName(fieldName)}
          fieldData={fieldData}
          isNestedFieldName={isNestedFieldName}
        ></InputText>
      )
    // ROLE_INPUT is a free-text Role picker: a themed MUI Autocomplete (freeSolo) offering the scenario
    // roles + "All roles" as type-ahead, with an inline chip previewing what the typed role resolves to.
    case FIELD_TYPES.ROLE_INPUT:
      return (
        <InputRole
          id={fieldData.id}
          label={fieldData.label}
          name={fieldName}
          placeholder={fieldData.placeholder}
          isRequired={isFieldRequired(fieldData)}
          errorMessage={getErrorMessageForFieldName(fieldName)}
          fieldData={fieldData}
          isNestedFieldName={isNestedFieldName}
        ></InputRole>
      )
    case FIELD_TYPES.EMAIL:
      return (
        <InputEmail
          id={fieldData.id}
          label={fieldData.label}
          name={fieldName}
          value={fieldData.value}
          placeholder={fieldData.placeholder}
          isRequired={isFieldRequired(fieldData)}
          errorMessage={getErrorMessageForFieldName(fieldName)}
          fieldData={fieldData}
          isNestedFieldName={isNestedFieldName}
        ></InputEmail>
      )
    case FIELD_TYPES.OPTION_CARDS:
      return (
        <InputRadioCards
          id={fieldData.id}
          label={fieldData.label}
          name={fieldName}
          optionData={fieldData.options}
          isRequired={isFieldRequired(fieldData)}
          errorMessage={getErrorMessageForFieldName(fieldName)}
          fieldData={fieldData}
          isNestedFieldName={isNestedFieldName}
        ></InputRadioCards>
      )
    case FIELD_TYPES.OPTIONS:
    case FIELD_TYPES.OPTIONS_WITH_STRING_OTHER:
      return (
        <InputRadio
          id={fieldData.id}
          label={fieldData.label}
          name={fieldName}
          rows={fieldData.rows}
          optionData={fieldData.options}
          isRequired={isFieldRequired(fieldData)}
          errorMessage={getErrorMessageForFieldName(fieldName)}
          fieldData={fieldData}
          isNestedFieldName={isNestedFieldName}
        ></InputRadio>
      )
    case FIELD_TYPES.MULTI_OPTIONS:
    case FIELD_TYPES.MULTI_OPTIONS_WITH_STRING_OTHER:
      return (
        <InputCheckbox
          id={fieldData.id}
          label={fieldData.label}
          name={fieldName}
          rows={fieldData.rows}
          optionData={fieldData.options}
          isRequired={isFieldRequired(fieldData)}
          errorMessage={getErrorMessageForFieldName(fieldName)}
          fieldData={fieldData}
          isNestedFieldName={isNestedFieldName}
        ></InputCheckbox>
      )
    case FIELD_TYPES.MULTI_DROPDOWN:
      return (
        <InputMultiSelect
          id={fieldData.id}
          label={fieldData.label}
          name={fieldName}
          optionData={fieldData.options}
          isRequired={isFieldRequired(fieldData)}
          errorMessage={getErrorMessageForFieldName(fieldName)}
          fieldData={fieldData}
          isNestedFieldName={isNestedFieldName}
        ></InputMultiSelect>
      )
    case FIELD_TYPES.FILE:
      return (
        <InputFile
          id={fieldData.id}
          label={fieldData.label}
          name={fieldName}
          value={fieldData.value}
          isRequired={isFieldRequired(fieldData)}
          errorMessage={getErrorMessageForFieldName(fieldName)}
          fieldData={fieldData}
          isNestedFieldName={isNestedFieldName}
          enablePreview={true}
        ></InputFile>
      )
    default:
      return null
  }
}

export const RenderFormGroups = (
  groupDataList,
  values,
  touched,
  errors,
  timeZones = null,
  shadowFormNodeId = null,
  formElementsTargetNode = null,
  // Optional map of `group.card` → DOM node. When provided, each group is
  // portalled into the node matching its `card` tag (groups without a matching
  // tag render inline). This lets a single Formik form distribute its groups
  // across separate card containers without splitting the form — value/onChange
  // wiring, validation and submit are all unchanged. Purely additive.
  groupTargets = null
) => {
  const renderedFormGroups =
    Array.isArray(groupDataList) &&
    groupDataList.map((formGroup, group_index) => {
      // Determine the field list item schema for the group from the child fields
      const groupItemSchema = Object.fromEntries(
        formGroup?.fields?.filter((aField) => aField.type !== FIELD_TYPES.ACTION_BTN).map((aField) => [aField.name, aField.value ?? ""]) ?? []
      )

      // Inject metadata for table group content
      const fieldTableValueMapper = (aValue) => {
        // Inject "__id"
        const existingId = aValue?.[tableGroupValueIdKey]

        if (aValue && !existingId) {
          const mappedValue = { ...aValue }
          mappedValue[tableGroupValueIdKey] = uuidV4()
          return mappedValue
        }

        return aValue
      }

      // When a per-group target node is supplied for this group's `card` tag,
      // portal the group into that node. Groups without a matching tag render
      // inline in the form's default position.
      const groupTargetNode = groupTargets && formGroup.card ? groupTargets[formGroup.card] : null

      const groupContent = (
        <div className={`row`} key={`${group_index}-${formGroup.id}`}>
          {formGroup.label ? (
            <div key={formGroup.id} className="col-12">
              <p className="field-label">
                {formGroup.label}
                {/* Optional faint suffix (e.g. "— optional"). Additive: only when set. */}
                {formGroup.labelSuffix ? <span className="field-label-suffix"> {formGroup.labelSuffix}</span> : null}
              </p>
            </div>
          ) : null}
          {formGroup.type && formGroup.type === FIELD_GROUP_TYPES.FIELDS ? (
            formGroup.fields &&
            Array.isArray(formGroup.fields) &&
            // Optionally wrap the field columns in a bordered container (e.g. the
            // solo "Players" table). Additive: only when fieldsWrapperClass is set;
            // otherwise the fields render as plain columns exactly as before.
            (formGroup.fieldsWrapperClass ? (
              <div className={`col-12`}>
                <div className={`row ${formGroup.fieldsWrapperClass}`}>
                  {formGroup.fields
                    .filter((f) => activeWhenMatches(f.activeWhen, values))
                    .map((formField) => (
                      <div key={`${formGroup.id}-${formField.id}`} className={`col-${formField.width}`}>
                        {formField.name === "time_zone"
                          ? GetFormElements(formField, formField.name, errors, timeZones)
                          : GetFormElements(formField, formField.name, errors)}
                      </div>
                    ))}
                </div>
              </div>
            ) : (
              formGroup.fields
                .filter((f) => activeWhenMatches(f.activeWhen, values))
                .map((formField) => (
                  <div key={`${formGroup.id}-${formField.id}`} className={`col-${formField.width}`}>
                    {formField.name === "time_zone"
                      ? GetFormElements(formField, formField.name, errors, timeZones)
                      : GetFormElements(formField, formField.name, errors)}
                  </div>
                ))
            ))
          ) : formGroup.type === FIELD_GROUP_TYPES.LIST ? (
            <FieldList
              id={formGroup.id}
              name={formGroup.name}
              field_list={values[formGroup.name]}
              field_list_schema={groupItemSchema}
              errorMessage={"Empty List"}
              fieldData={formGroup.fields}
            ></FieldList>
          ) : formGroup.type === FIELD_GROUP_TYPES.TABLE ? (
            <FieldTable
              id={formGroup.id}
              name={formGroup.name}
              table_data={values[formGroup.name]?.map((aValue) => fieldTableValueMapper(aValue)) ?? []}
              table_schema={groupItemSchema}
              errorMessage={"Empty List"}
              fieldData={formGroup.fields}
              groupLabel={formGroup.label}
              shadowFormNodeId={shadowFormNodeId}
              isExportEnabled={formGroup.export_enabled ?? false}
              isImportEnabled={formGroup.import_enabled ?? false}
              isMutable={formGroup.mutable ?? true}
            ></FieldTable>
          ) : formGroup.type === FIELD_GROUP_TYPES.ROSTER ? (
            <RosterTable
              id={formGroup.id}
              name={formGroup.name}
              fieldData={formGroup.fields}
              groupLabel={formGroup.label}
              isExportEnabled={formGroup.export_enabled ?? false}
              isImportEnabled={formGroup.import_enabled ?? false}
            ></RosterTable>
          ) : (
            <>NO MATCHING GROUP TYPE</>
          )}
          {/* Optional muted hint line under the group (e.g. the Players / Observers
              "✦ …" explainer). Additive: only rendered when formGroup.hint is set. */}
          {formGroup.hint ? (
            <div className="col-12">
              <div className="field-group-hint">
                <span className="field-group-hint-star">✦</span>
                {formGroup.hint}
              </div>
            </div>
          ) : null}
        </div>
      )

      // Portal this individual group into its assigned card node when one exists.
      if (groupTargetNode) {
        return createPortal(groupContent, groupTargetNode, `portal-${group_index}-${formGroup.id}`)
      }
      return groupContent
    })
  if (formElementsTargetNode) {
    // Create a portal for the form content
    return createPortal(renderedFormGroups, formElementsTargetNode)
  }

  return renderedFormGroups
}
